import { useEffect, useState } from 'react';

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Typography,
} from "@mui/material";
import { useCompanyHook } from '../../Hooks/Companies/companyHooks';
import { IconPlus, IconEdit, IconEye, IconTrash, IconPhone, IconMapPin } from '@tabler/icons-react';

import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import RouteHelper from '../../Utilities/Helpers/RouteHelper';
import DateHelper from '../../Utilities/Helpers/DateHelper';
import AccessibilityHelper from '../../Utilities/Helpers/AccessibilityHelper';
import LocalStorageHelper from '../../Utilities/Helpers/LocalStorageHelper';

import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { ButtonType } from '../../Utilities/Enums/ButtonTypeEnum';
import { ViewType } from '../../Utilities/Enums/ViewTypeEnum';
import { CompanyListField, AuthenticationField } from '../../Utilities/Enums/ParametersEnum';
import { AdminModules } from '../../Utilities/Enums/AdminModulesEnum';
import { OrderByEnum } from '../../Utilities/Enums/SortEnums';
import { RoleTypeEnum } from '../../Utilities/Enums/RolesEnums';

import { CompanyList } from '../../Models/Company/Company';
import { Authentication } from "../../Models/Authentication/Authentication";

import CustomListComponent from '../../CustomViews/Pages/ListComponent/CustomListComponent';
import CustomIconButton from '../../CustomViews/Elements/CustomIconButton';
import CustomDeleteConfirmation from '../../CustomViews/Elements/CustomDeleteConfirmation/CustomDeleteConfirmation';
import CustomProgressBar from '../../CustomViews/Elements/CustomProgressBar/CustomProgressBar';
import CustomButton from '../../CustomViews/Elements/CustomButton';
import { TableContainer } from '../../Styles/CustomViews/Pages/ListComponent/customListComponentStyles';
import CustomListHeader from '../../CustomViews/Pages/ListComponent/CustomListHeader';
import CustomListSearch from '../../CustomViews/Pages/ListComponent/Search/CustomListSearch';
import FormDatePicker from '../../CustomViews/Elements/FormDatePicker';
import NewTab from '../../CustomViews/Pages/NewTab/NewTab';
import VendorsDropdown from '../../CustomViews/Elements/AutoComplete/VendorsDropdown';

import styled from 'styled-components';
import { CustomListSearchResetBtns } from '../../CustomViews/Pages/ListComponent/Search/CustomListSearchResetBtns';

const ContactPersonContainer = styled.div<{}>`
    .contactDetailItem {
        width: 100%;
        font-size: 0.75rem;
        font-weight: 600;
        display: grid;
        grid-template-columns: 0.1fr 1.5fr;
        align-items: start;

        svg {
            width: 12px;
            height: 12px;
            margin: 3px 5px 0px 0px;
        }
    }

    .contactName {
        // grid-column: span 2;
        display: unset;
        grid-template-columns: unset;
    }

    .subContactDetail {
        font-weight: 400;
        color: grey;
    }
`;

export default function CompaniesList() {
    const navigate = useNavigate()
    const location = useLocation()
    let _userData: Authentication = LocalStorageHelper.getUserData();

    //search params
    const queryParams = new URLSearchParams(location.search);
    const [searchParams, setSearchParams] = useSearchParams(queryParams);

    //states
    const [queryStr, setQueryStr] = useState({
        pageSize: QueryBuilderHelper.DefaultPageSizeValue(),
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        lastUpdatedDate: QueryBuilderHelper.DefaultQueryFilterValue('lastUpdatedDate', null),
        sortBy: QueryBuilderHelper.DefaultSortValue(CompanyListField.NAME),
        orderBy: QueryBuilderHelper.DefaultOrderValue(OrderByEnum.DESC),
        vendorPublicId: QueryBuilderHelper.DefaultQueryFilterValue('vendorPublicId', null),
    });
    const [openDelete, setopenDelete] = useState(false);
    let [toDeleteItem, settoDeleteItem] = useState<any>(null);

    const q_queryKey = [CachingEnum.COMPANIES, queryStr]
    const { data, fetchStatus, editCompanyById, deleteCompanyById } = useCompanyHook(q_queryKey, queryStr);

    const tableColumns = [
        {
            accessor: CompanyListField.NAME,
            title: 'Company Name',
            width: '20%',
            sortable: true,
            render: (record: CompanyList) => (record?.[CompanyListField.PUBLIC_ID] ? 
                <NewTab 
                linkTitle={record?.[CompanyListField.NAME] != null && record?.[CompanyListField.NAME] != "" ? record?.[CompanyListField.NAME] : "--"}
                linkUrl={`${RouteEnum.COMPANY_DETAILS}?id=${record?.[CompanyListField.PUBLIC_ID] }&view=${ViewType.PREVIEW}`} /> : "--"
            ),
        },
        {
            accessor: CompanyListField.CONTACT_PERSON,
            title: "Contact Person",
            sortable: true,
            width: '15%',
            render: (record: CompanyList) => <ContactPersonContainer>
                <Typography className="contactDetailItem contactName">{record?.[CompanyListField.CONTACT_PERSON] ?? ""}</Typography>
                {record?.[CompanyListField.CONTACT_PHONE] && <Typography className="contactDetailItem subContactDetail"><IconPhone />{record?.[CompanyListField.CONTACT_PHONE] ?? ""}</Typography>}
                {record?.[CompanyListField.FULL_ADDRESS] && <Typography className="contactDetailItem subContactDetail"><IconMapPin />{record?.[CompanyListField.FULL_ADDRESS] ?? ""}</Typography>}
            </ContactPersonContainer>
        },
        {
            accessor: CompanyListField.LICENSE_KEY,
            title: 'License Key',
            sortable: true,
            render: (record: CompanyList) => record?.[CompanyListField.LICENSE_KEY] ?? "--"
        },
        {
            accessor: CompanyListField.STORAGE_USED,
            title: 'Data Storage',
            sortable: true,
            render: (record: CompanyList) => <CustomProgressBar color="info" valueAmount={record?.[CompanyListField.STORAGE_USED] ?? 0} overAmount={record?.[CompanyListField.STORAGE_CAPACITY] ?? 0} progressTitle={"GB"}/>,
        },
        {
            accessor: CompanyListField.USER_SLOT_USED,
            title: 'Slots Used',
            sortable: true,
            render: (record: CompanyList) => <CustomProgressBar color="warning" valueAmount={record?.[CompanyListField.USER_SLOT_USED] ?? 0} overAmount={record?.[CompanyListField.USER_CAPACITY] ?? 0} progressTitle={"User(s)"}/>,
        },
        {
            accessor: CompanyListField.UPDATED_DATE,
            title: 'Last Updated',
            sortable: true,
            render: (record: CompanyList) => record?.[CompanyListField.UPDATED_DATE] ? DateHelper.getFormattedDate(record?.[CompanyListField.UPDATED_DATE], "dd/MM/yyyy hh:mm a") : "--",
        },
        {
            accessor: 'action',
            title: "Action",
            render: (record: CompanyList) => <>
                <CustomIconButton toolTipName='View'
                    icon={<IconEye />}
                    onClick={(e: any) => { 
                        navigate(`${RouteEnum.COMPANY_DETAILS}?id=${record?.[CompanyListField.PUBLIC_ID]}&view=${ViewType.PREVIEW}`, { replace: true })
                    }} />
                {AccessibilityHelper.hasAccess(AdminModules.COMPANY_EDIT) && <CustomIconButton toolTipName='Edit'
                    icon={<IconEdit />}
                    onClick={(e: any) => {
                        navigate(`${RouteEnum.COMPANY_DETAILS}?id=${record?.[CompanyListField.PUBLIC_ID]}&view=${ViewType.EDIT}`, { replace: true })
                    }} /> }
                {AccessibilityHelper.hasAccess(AdminModules.COMPANY_DELETE) && <CustomIconButton toolTipName='Delete'
                    icon={<IconTrash color='var(--buttonHoverBGDangerColor)' />}
                    onClick={(e: any) => { 
                        setopenDelete(true);
                        settoDeleteItem(record);
                    }} /> }
            </>
        },
    ];

     useEffect(() => {
        setSearchParams(QueryBuilderHelper.CleanQueryDictionary(queryStr));
    }, [queryStr]);
    
    return <TableContainer>
        <CustomListHeader
            title={RouteHelper.getRouteDescription(RouteEnum.COMPANIES)}
            headerButtons={<>
                {AccessibilityHelper.hasAccess(AdminModules.COMPANY_CREATE) && <CustomButton
                    btnType={ButtonType.PRIMARY}
                    title='Add New'
                    startIcon={<IconPlus />}
                    // isRoundedCorner={true}
                    onClick={(e: any) => {
                        navigate(`${RouteEnum.COMPANY_DETAILS}?id=0&view=${ViewType.ADD}`, { replace: true })
                    }} /> }
            </> } />
        <CustomListSearch searchValue={queryStr.searchString ?? ""}
            onChangeSearch={(searchString) => setQueryStr(prev => ({ ...prev, searchString: searchString, pageNumber: 1 }))}
            searchPlaceholder='Search Company / Contact Person / License Key'>
            <FormDatePicker name="companyUpdatedDate"
                hideLabel
                title=''
                placeholder='Last Updated Date'
                fieldValue={queryStr.lastUpdatedDate}
                handleOnChange={(date: Date) => setQueryStr(prev => ({
                    ...prev,
                    lastUpdatedDate: date ?? DateHelper.getFormattedDate(new Date(), "yyyy-MM-dd"),
                    pageNumber: 1,
                }))} />

                {(_userData?.[AuthenticationField.ROLE_TYPE] == RoleTypeEnum.VENDOR_ADMIN || _userData?.[AuthenticationField.ROLE_TYPE] == RoleTypeEnum.SUPER_USER) && <VendorsDropdown name="vendorPublicId"
                    hideLabel
                    title=''
                    placeholder='Vendor'
                    fieldValue={queryStr.vendorPublicId}
                    isUserVendorsOnly={_userData?.[AuthenticationField.ROLE_TYPE] == RoleTypeEnum.VENDOR_ADMIN}
                    handleOnChange={(selectedValue: any) => setQueryStr(prev => ({
                        ...prev,
                        vendorPublicId: selectedValue?.id ?? null,
                        pageNumber: 1,
                    }))} />}
            <CustomListSearchResetBtns onClickSearch={() => { }}
                onClickReset={() => {
                    setQueryStr(prev => ({
                        ...prev,
                        searchString: '',
                        lastUpdatedDate: null,
                        vendorPublicId: null,
                        pageNumber: 1,
                     }))
                }}/>
        </CustomListSearch>
        <CustomListComponent
            fetching={fetchStatus == "fetching"}
            columns={tableColumns}
            idAccessor={(record: CompanyList) => `company${record?.[CompanyListField.PUBLIC_ID]}`}
            records={data?.data?.list ?? []}
            totalRecords={data?.data?.metaData?.totalCount ?? 0}
            page={queryStr.pageNumber}
            recordsPerPage={queryStr.pageSize}
            defaultColumnSort={{ columnAccessor: queryStr?.sortBy ?? 'id', direction: queryStr?.orderBy ?? 'asc' }}
            onSortStatusChange={((sort: any) => { setQueryStr((prev: any) => ({ ...prev, sortBy: sort?.columnAccessor ?? "id", orderBy: sort?.direction ?? "asc", pageNumber: 1 })) })}
            setPage={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageNumber: p }))}
            onRecordsPerPageChange={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageSize: p, pageNumber: 1 }))}
           />

        {openDelete && <CustomDeleteConfirmation
            openDialog={openDelete}
            ItemToDelete={toDeleteItem?.[CompanyListField.NAME] ?? null} // Optional to display name of the item to be deleted
            handleClose={() => {
                setopenDelete(false);
            }}
            showRemarksField={true}
            deleteItem={async (remarks: any) => {
                if(toDeleteItem?.[CompanyListField.PUBLIC_ID]) {
                    let deleteReponse: any = await deleteCompanyById(toDeleteItem?.[CompanyListField.PUBLIC_ID], remarks);

                    if(deleteReponse?.isSuccess) {
                        setopenDelete(false);
                        // navigate(RouteEnum.COMPANIES, { replace: true });
                    }
                }
            }} />
        }
    </TableContainer>
}
